"use strict";
import { ChainGate } from "components/ChainGate";
import ErrorBoundary from "components/ErrorBoundary";
import { useFeatureFlagUrlOverrides } from "featureFlags/useFeatureFlagUrlOverrides";
import { useAtom } from "jotai";
import { Body } from "pages/App/Body";
import { AppLayout } from "pages/App/Layout";
import { ResetPageScrollEffect } from "pages/App/utils/ResetPageScroll";
import { UserPropertyUpdater } from "pages/App/utils/UserPropertyUpdater";
import { useEffect, useLayoutEffect } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { shouldDisableNFTRoutesAtom } from "state/application/atoms";
import DarkModeQueryParamReader from "theme/components/DarkModeQueryParamReader";
import { initializeScrollWatcher } from "components/modals/ScrollLock";
import { EXTENSION_PASSKEY_AUTH_PATH } from "features/passkey/constants";
import Trace from "features/telemetry/Trace";
import { isPathBlocked } from "utils/blockedPaths";
import { MICROSITE_LINK } from "utils/openDownloadApp";
import { getCurrentPageFromLocation } from "utils/urlRoutes";
import { useWindowFunctions } from "utils/windowFunctions";
import MetaData from "./MetaData";
const OVERRIDE_PAGE_LAYOUT = [EXTENSION_PASSKEY_AUTH_PATH];
export default function App() {
  const [, setShouldDisableNFTRoutes] = useAtom(shouldDisableNFTRoutesAtom);
  const location = useLocation();
  const { pathname } = location;
  const currentPage = getCurrentPageFromLocation(pathname);
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("disableNFTs") === "true") {
      setShouldDisableNFTRoutes(true);
    } else if (searchParams.get("disableNFTs") === "false") {
      setShouldDisableNFTRoutes(false);
    }
  }, [searchParams, setShouldDisableNFTRoutes]);
  useFeatureFlagUrlOverrides();
  useWindowFunctions();
  useEffect(() => {
    initializeScrollWatcher();
  }, []);
  const shouldRedirectToAppInstall = pathname?.startsWith("/address/");
  useLayoutEffect(() => {
    if (shouldRedirectToAppInstall) {
      window.location.href = MICROSITE_LINK;
    }
  }, [shouldRedirectToAppInstall]);
  if (shouldRedirectToAppInstall) {
    return null;
  }
  const shouldBlockPath = isPathBlocked(pathname);
  if (shouldBlockPath && pathname !== "/swap") {
    return <Navigate to="/swap" replace />;
  }
  const shouldOverridePageLayout = OVERRIDE_PAGE_LAYOUT.includes(pathname);
  return <ErrorBoundary>
      <DarkModeQueryParamReader />
      <Trace page={currentPage}>
        {
    /*
      This is where *static* page titles are injected into the <head> tag. If you
      want to set a page title based on data that's dynamic or not available on first render,
      you can set it later in the page component itself, since react-helmet-async prefers the most recently rendered title.
    */
  }
        <MetaData />
        <UserPropertyUpdater />
        <ResetPageScrollEffect />
        <ChainGate>
          {shouldOverridePageLayout ? <Body shouldRenderAppChrome={false} /> : <AppLayout />}
        </ChainGate>
      </Trace>
    </ErrorBoundary>;
}
